import {
  ActionButton,
  CompoundButton,
  IButtonStyles,
  IIconStyles,
  IStackSlots,
  IStyle,
  mergeStyles,
  Stack
} from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import { DocumentOnePageSparkleRegular } from "@fluentui/react-icons";
import { CSSProperties } from "react";
import CustomPanel from "../SharedComponents/CustomPanel/CustomPanel";
import { useLazyGetCRMEngagementAISummaryByCustomerProductIdQuery } from "../slices/ExploreCustomers.slice";
import { logEvent } from "./AppInsightsHelper";
import DisplayAPIsException from "../BasicComponents/DisplayAPIsExceptionAlert";

export function CRMEngagementAISummaryPanel({
  customerProductId,
  isAISummaryPresent,
  pageName
}: Readonly<{ customerProductId: string; isAISummaryPresent: boolean; pageName?: string }>) {
  // Properties
  const [enablePanel, { setTrue: openPanel, setFalse: dismissPanel }] = useBoolean(false);
  const [getAISummary, { data, isLoading, error }] = useLazyGetCRMEngagementAISummaryByCustomerProductIdQuery();

  const customProps = {
    customerProductId: customerProductId
  };

  // Events
  const handleDisplayAISummary = () => {
    openPanel();
    getAISummary({ customerProductId }, true);
    logEvent("Customer Engagements AI Summary Panel", pageName ?? "", "CRMEngagementAISummaryPanel",  customProps);
  };

  const formatKey = (key: string) => {
    return key
      .replace(/([a-z])([A-Z])/g, "$1 $2") // insert space before capital letters
      .replace(/^./, (str) => str.toUpperCase()); // capitalize the first letter
  };

  const SUMMARY_NOT_AVAILABLE = (
    <div className={STYLES.textContainerDescription}>AI summary not available for this product.</div>
  );

  const renderCommandButtonItems = () => {
    if (error) {
      return <DisplayAPIsException error={error} height={250} />;
    }
    if (!data) {
      return SUMMARY_NOT_AVAILABLE;
    }
    return Object.entries(data).map(([key, value]) => (
      <Stack key={key} styles={STYLES.aiInsightsPanelContent}>
        <CompoundButton key={key} styles={STYLES.commandButton}>
            {renderLabelWithDescription({ text: formatKey(key), description: value })}
          </CompoundButton>
      </Stack>
    ));
  };

  return (
    <Stack styles={{ root: { height: "100%" } }}>
      <ActionButton
        ariaLabel="AI Summary"
        iconProps={{ iconName: "CRMCustomerInsightsApp" }}
        styles={STYLES.actionButton}
        onClick={handleDisplayAISummary}
        disabled={!isAISummaryPresent}
        title={!isAISummaryPresent ? "No summary available." : "Click here to see the AI-generated summary."}
      />
      <CustomPanel
        dismissPanel={dismissPanel}
        enablePanel={enablePanel}
        isLoading={isLoading}
        panelTitle="AI Summary - Customer Engagement"
      >
        {renderCommandButtonItems()}
      </CustomPanel>
    </Stack>
  );
}

// Constants
const commandButtonBorderColor = "#0f6cbd";
const commandButtonColor: string = "rgb(26, 27, 25)";

export const STYLES = {
  commandButton: {
    root: {
      backgroundColor: "white",
      border: "1px solid #d1d1d1",
      borderRadius: 16,
      boxShadow: "0 0 2px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.14)",
      cursor: "default",
      color: commandButtonColor,
      maxWidth: "none",
      minHeight: "auto",
      padding: "10px 20px 1px",
      transition: "transform .2s ease-in-out",
      userSelect: "text"
    },
    rootHovered: {
      backgroundColor: "white !important",
      border: `1px solid ${commandButtonBorderColor}`,
      color: `${commandButtonColor} !important`,
      transform: "translateY(-2px)"
    },
    flexContainer: { display: "block" }
  } as IButtonStyles,
  textContainer: mergeStyles({
    flexGrow: 1,
    display: "block",
    textAlign: "left"
  } as IStyle),
  textContainerLabel: mergeStyles({
    color: commandButtonColor,
    cursor: "text",
    display: "block",
    fontWeight: 600,
    lineHeight: "20px"
  } as IStyle),
  textContainerLabelIcon: {
    color: commandButtonBorderColor,
    fontSize: 20,
    fontWeight: 600
  } as CSSProperties,
  textContainerDescription: mergeStyles({
    color: commandButtonColor,
    cursor: "text",
    fontSize: 14,
    fontWeight: 400,
    letterSpacing: "normal",
    lineHeight: "20px",
    maxHeight: 750,
    overflowY: "auto",
    whiteSpace: "pre-wrap"
  } as IStyle),
  textContainerDescriptionContent: {
    root: {
      borderRadius: ".6em",
      display: "inline-flex",
      fontWeight: 600,
      gridColumnGap: 8,
      padding: ".2em .8em"
    }
  } as IStackSlots,
  textContainerDescriptionContentIcon: {
    root: {
      fontWeight: 600
    }
  } as IIconStyles,
  actionButton: { root: { height: "100%" }, icon: { fontSize: 20 } } as IButtonStyles,
  aiInsightsPanelContent: { root: { marginTop: 24, gridRowGap: "12px" } } as IStackSlots
};

// Functions
export function renderLabelWithDescription({ text, description }: { text: string; description: string }) {
  const descriptionItems = description ? (
    description.split(". ").map((item, index) => <p key={index}>{item}</p>)
  ) : (
    <p>N/A</p>
  );

  return (
    <span className={STYLES.textContainer}>
      <span className={STYLES.textContainerLabel}>
        <Stack horizontal tokens={{ childrenGap: 8 }}>
          <DocumentOnePageSparkleRegular style={STYLES.textContainerLabelIcon} />
          <span>{text}</span>
        </Stack>
      </span>
      <div className={STYLES.textContainerDescription}>{descriptionItems}</div>
    </span>
  );
}
